"use client";

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client'; // Use supabase for realtime
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Check, Trash2, Tag, CheckCircle, DollarSign, Briefcase, MessageCircle, Search } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';

interface Notification {
  id: string;
  user_id: string;
  type: 'task_assigned' | 'new_message' | 'invoice_ready' | 'project_update' | 'billing';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  related_id?: string;
  category?: string;
}

// Add mapping so categories match stored types
const TYPE_MAP = {
  task: 'task_assigned',
  billing: 'billing',
  project: 'project_update',
  message: 'new_message',
} as const;

export const NotificationsPage = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'task' | 'billing' | 'project' | 'message'>('all');

  // NEW: search and pagination
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (!profile) {
      navigate('/dashboard');
      return;
    }
    console.log('Notifications: Profile loaded, ID:', profile.id);
  }, [profile, navigate]);

  // Refetch on category/search change (reset pagination)
  useEffect(() => {
    setPage(1);
  }, [selectedCategory, search]);

  useEffect(() => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }

    const fetchNotifications = async (opts?: { append?: boolean }) => {
      try {
        console.log('Notifications: Fetching for user', profile.id, 'category:', selectedCategory, 'search:', search, 'page:', page);
        setError(null);

        const from = 0;
        const to = page * PAGE_SIZE - 1;

        let query = supabase
          .from('notifications')
          .select('*')
          .eq('user_id', profile.id)
          .order('created_at', { ascending: false })
          .range(from, to);

        if (selectedCategory !== 'all') {
          query = query.eq('type', TYPE_MAP[selectedCategory]);
        }

        if (search.trim().length > 0) {
          const s = search.trim();
          query = query.or(`title.ilike.%${s}%,message.ilike.%${s}%`);
        }

        const { data, error: queryError } = await query;

        if (queryError) {
          console.error('Notifications: Query error:', queryError);
          throw queryError;
        }

        console.log('Notifications: Fetched', data?.length || 0, 'notifications');
        // When paginating, we always send full slice 0..to, so replace state
        setNotifications(data || []);
      } catch (err: any) {
        console.error('Notifications: Fetch failed:', err);
        setError(`Failed to load notifications: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Realtime subscription
    let channel: ReturnType<typeof supabase.channel> | null = null;
    try {
      channel = supabase
        .channel('notifications')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${profile.id}` },
          (payload) => {
            console.log('Notifications: Realtime update:', payload.eventType);
            if (payload.eventType === 'INSERT') {
              const newNotif = payload.new as Notification;
              setNotifications(prev => [newNotif, ...prev]);
              toast.custom((t) => (
                <div className="flex items-center space-x-2 p-4 bg-primary text-primary-foreground rounded-lg">
                  <Bell className="h-4 w-4" />
                  <div>
                    <strong>{newNotif.title}</strong>
                    <p className="text-sm">{newNotif.message}</p>
                  </div>
                </div>
              ), {
                duration: 5000,
                action: {
                  label: 'View',
                  onClick: () => markAsRead(newNotif.id),
                },
              });
            } else {
              // For non-insert changes, refetch the current slice to stay consistent
              setLoading(true);
              setError(null);
              // Reuse current filters/page
              const refetch = async () => {
                let query = supabase
                  .from('notifications')
                  .select('*')
                  .eq('user_id', profile.id)
                  .order('created_at', { ascending: false })
                  .range(0, page * PAGE_SIZE - 1);

                if (selectedCategory !== 'all') {
                  query = query.eq('type', TYPE_MAP[selectedCategory]);
                }
                if (search.trim().length > 0) {
                  const s = search.trim();
                  query = query.or(`title.ilike.%${s}%,message.ilike.%${s}%`);
                }
                const { data } = await query;
                setNotifications(data || []);
                setLoading(false);
              };
              refetch();
            }
          }
        )
        .subscribe((status: any) => {
          if (status === 'SUBSCRIPTION_ERROR') {
            console.error('Notifications subscription error:', status);
            toast.error('Live notifications unavailable. Refresh to retry.');
          }
        });
    } catch (error) {
      console.error('Failed to subscribe to notifications:', error);
      toast.error('Failed to connect to live notifications.');
    }

    return () => {
      if (channel) supabase.removeChannel(channel);
      console.log('Notifications: Cleanup - unsubscribed');
    };
  }, [profile?.id, selectedCategory, search, page]);

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id);
      if (!error) {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        toast.success('Notification marked as read!');
      } else {
        console.error('Mark as read failed:', error);
      }
    } catch (err: any) {
      console.error('Mark as read error:', err);
      toast.error('Failed to mark as read');
    }
  };

  // NEW: Mark all as read
  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', profile?.id)
        .eq('read', false);
      if (!error) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        toast.success('All notifications marked as read!');
      } else {
        console.error('Mark all as read failed:', error);
      }
    } catch (err: any) {
      console.error('Mark all as read error:', err);
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      const { error } = await supabase.from('notifications').delete().eq('id', id);
      if (!error) {
        setNotifications(prev => prev.filter(n => n.id !== id));
        toast.success('Notification deleted!');
      } else {
        console.error('Delete failed:', error);
      }
    } catch (err: any) {
      console.error('Delete error:', err);
      toast.error('Failed to delete notification');
    }
  };

  // NEW: Clear all read
  const clearReadNotifications = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', profile?.id)
        .eq('read', true);
      if (!error) {
        setNotifications(prev => prev.filter(n => !n.read));
        toast.success('Cleared read notifications!');
      } else {
        console.error('Clear read failed:', error);
      }
    } catch (err: any) {
      console.error('Clear read error:', err);
      toast.error('Failed to clear read notifications');
    }
  };

  const categories = [
    { value: 'all' as const, label: 'All', icon: Bell },
    { value: 'task' as const, label: 'Tasks', icon: CheckCircle },
    { value: 'billing' as const, label: 'Billing', icon: DollarSign },
    { value: 'project' as const, label: 'Projects', icon: Briefcase },
    { value: 'message' as const, label: 'Messages', icon: MessageCircle },
  ];

  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Bell className="h-5 w-5 animate-pulse" />
                <span>Loading notifications...</span>
              </div>
              <Badge variant="secondary" className="animate-pulse">0 unread</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4 bg-muted rounded-md animate-pulse">
                <div className="h-4 bg-muted-foreground/20 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted-foreground/20 rounded w-1/2"></div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-destructive">
              <Bell className="h-5 w-5" />
              <span>Notifications Error</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mx-auto p-4 max-w-2xl">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-muted-foreground">Profile not loaded. Redirecting...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // FIX: filter using mapped type
  const filteredNotifications = notifications.filter(n => {
    if (selectedCategory === 'all') return true;
    return n.type === TYPE_MAP[selectedCategory];
  });

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center space-x-2">
              <Bell className="h-5 w-5" />
              <span>Notifications</span>
            </span>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{notifications.filter(n => !n.read).length} unread</Badge>
              {/* NEW bulk actions */}
              <Button variant="outline" size="sm" onClick={markAllAsRead}>
                Mark all as read
              </Button>
              <Button variant="outline" size="sm" onClick={clearReadNotifications}>
                Clear read
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {/* NEW: search input */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notifications..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((cat) => (
              <Button
                key={cat.value}
                variant={selectedCategory === cat.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.value)}
                className="flex items-center space-x-1"
              >
                <cat.icon className="h-3 w-3" />
                <span>{cat.label}</span>
              </Button>
            ))}
          </div>
          {filteredNotifications.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No {selectedCategory} notifications yet.</p>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => (
                <Card key={notification.id} className={!notification.read ? 'border-primary/50 bg-primary/5' : ''}>
                  <CardContent className="pt-6 pb-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium">{notification.title}</h4>
                        <Badge variant="secondary" className="mt-1">{notification.type}</Badge>
                        <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                          <span>{format(new Date(notification.created_at), 'MMM dd, yyyy HH:mm')}</span>
                          {!notification.read && <Badge variant="destructive">Unread</Badge>}
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-auto">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(notification.id)}
                          disabled={notification.read}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteNotification(notification.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {/* NEW: Load more pagination */}
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(prev => prev + 1)}
                >
                  Load more
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;